import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  Modal,
  notification,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  SearchOutlined,
  TableOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { getTariffListRowKey } from './tariffRowKeys';
import DashboardHero from '../../features/dashboard/components/DashboardHero';
import DashboardSection from '../../features/dashboard/components/DashboardSection';
import { TableSkeleton } from '../../features/dashboard/components/DashboardSkeleton';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../../features/dashboard/utils';
import StandardTariffListingCard from './StandardTariffListingCard';
import './standard-tariff-dashboard.css';

const { Text } = Typography;

const getServerError = (e, fallback) =>
  (e?.response?.data?.message != null && e.response.data.message !== '')
    ? e.response.data.message
    : (e?.message || fallback);
const getServerMessage = (data, fallback) =>
  (data?.message != null && data.message !== '') ? data.message : fallback;

const getTariffId = (record) => record?.id ?? record?.tariff_id;

/**
 * StandardTariffView – HMO standard tariff (view, upload, scope/dates). Used at /admin/tariffs.
 */
const StandardTariffView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState('list');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState({ states: [], zones: [], facility_types: [], plans: [], locations: [] });
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTariffName, setUploadTariffName] = useState('');
  const [uploadEffectiveDate, setUploadEffectiveDate] = useState(null);
  const [uploadExpiryDate, setUploadExpiryDate] = useState(null);
  const [uploadScopeState, setUploadScopeState] = useState('');
  const [uploadScopeZoneId, setUploadScopeZoneId] = useState('');
  const [uploadScopeFacilityType, setUploadScopeFacilityType] = useState('');
  const [uploadScopeLocationId, setUploadScopeLocationId] = useState('');
  const [uploadScopePlanId, setUploadScopePlanId] = useState('');
  const [uploadCategoryId, setUploadCategoryId] = useState(undefined);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const fetchTariffs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const setListFromResponse = (body) => {
      const data = body?.data ?? body;
      let list = Array.isArray(data?.tariffs) ? data.tariffs : (data?.tariff ? [data.tariff] : []);
      if (list.length === 1 && data?.items_count != null) list[0].items_count = data.items_count;
      setTariffs(list);
    };
    try {
      if (typeof lib.getAdminStandardTariffs === 'function') {
        const resp = await lib.getAdminStandardTariffs();
        const body = resp?.data;
        if (!body?.error) {
          setListFromResponse(body);
          return;
        }
        try {
          const fallback = await lib.getAdminStandardTariff();
          if (!fallback?.data?.error) {
            setListFromResponse(fallback.data);
            return;
          }
        } catch {
          /* ignore */
        }
        setError((body?.message != null && body.message !== '') ? body.message : 'Unable to load standard tariffs');
        setTariffs([]);
        return;
      }
      const resp = await lib.getAdminStandardTariff();
      const body = resp?.data;
      if (body?.error) {
        setError((body?.message != null && body.message !== '') ? body.message : 'Unable to load standard tariffs');
        setTariffs([]);
      } else {
        setListFromResponse(body);
      }
    } catch (e) {
      try {
        const fallback = await lib.getAdminStandardTariff();
        if (!fallback?.data?.error) {
          setListFromResponse(fallback.data);
          return;
        }
      } catch {
        /* ignore */
      }
      setError(getServerError(e, 'Failed to load standard tariffs'));
      setTariffs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUploadSubmit = useCallback(async () => {
    if (!uploadFile || !uploadTariffName || !uploadEffectiveDate) {
      setUploadError('Please provide file, tariff name, and effective date.');
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('tariff_name', uploadTariffName);
      formData.append('effective_date', dayjs(uploadEffectiveDate).format('YYYY-MM-DD'));
      if (uploadExpiryDate) formData.append('expiry_date', dayjs(uploadExpiryDate).format('YYYY-MM-DD'));
      if (uploadCategoryId != null && uploadCategoryId !== '' && uploadCategoryId !== 'all') formData.append('upload_category_id', String(uploadCategoryId));
      if (uploadScopeState != null && String(uploadScopeState).trim()) formData.append('scope_state', String(uploadScopeState).trim());
      if (uploadScopeZoneId != null && uploadScopeZoneId !== '') formData.append('scope_zone_id', String(uploadScopeZoneId));
      if (uploadScopeFacilityType != null && String(uploadScopeFacilityType).trim()) formData.append('scope_facility_type', String(uploadScopeFacilityType).trim());
      if (uploadScopeLocationId != null && uploadScopeLocationId !== '') formData.append('scope_location_id', String(uploadScopeLocationId));
      if (uploadScopePlanId != null && uploadScopePlanId !== '') formData.append('scope_plan_id', String(uploadScopePlanId));
      const resp = await lib.uploadStandardTariff(formData);
      const data = resp?.data;
      if (data?.error) {
        setUploadError(getServerMessage(data, 'Upload failed'));
        return;
      }
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadTariffName('');
      setUploadEffectiveDate(null);
      setUploadExpiryDate(null);
      setUploadScopeState('');
      setUploadScopeFacilityType('');
      setUploadScopeLocationId('');
      setUploadScopePlanId('');
      setUploadCategoryId(undefined);
      fetchTariffs();
      const msg = getServerMessage(data, 'Standard tariff uploaded');
      const inserted = data?.data?.inserted ?? data?.inserted;
      const detail = inserted != null ? ` (${inserted} row${inserted !== 1 ? 's' : ''} inserted)` : '';
      notification.success({ message: msg + detail, placement: 'topRight' });
    } catch (e) {
      setUploadError(getServerError(e, 'Upload failed.'));
    } finally {
      setUploadLoading(false);
    }
  }, [uploadFile, uploadTariffName, uploadEffectiveDate, uploadExpiryDate, uploadCategoryId, uploadScopeState, uploadScopeZoneId, uploadScopeFacilityType, uploadScopeLocationId, uploadScopePlanId, fetchTariffs]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  useEffect(() => {
    lib.getTariffCategories()
      .then((resp) => {
        const body = resp?.data;
        const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
        setCategoryOptions(
          list
            .map((c) => ({ value: c.id ?? c.category_id, label: c.category_name ?? c.name ?? `Category ${c.id ?? c.category_id}` }))
            .filter((o) => o.value != null)
        );
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  useEffect(() => {
    if (!uploadModalOpen) return;
    setScopeOptionsLoading(true);
    lib.getStandardTariffScopeOptions()
      .then((resp) => {
        const body = resp?.data;
        const data = (body?.error === 0 && body?.data) ? body.data : body ?? {};
        setScopeOptions({
          states: Array.isArray(data.states) ? data.states : [],
          zones: Array.isArray(data.zones) ? data.zones : [],
          facility_types: Array.isArray(data.facility_types) ? data.facility_types : [],
          plans: Array.isArray(data.plans) ? data.plans : [],
          locations: Array.isArray(data.locations) ? data.locations : [],
        });
      })
      .catch(() => setScopeOptions({ states: [], zones: [], facility_types: [], plans: [], locations: [] }))
      .finally(() => setScopeOptionsLoading(false));
  }, [uploadModalOpen]);

  const toOption = (o) => (o && typeof o === 'object' && ('value' in o || 'id' in o)
    ? { value: o.value ?? o.id, label: o.label ?? o.zone_name ?? o.category_name ?? o.name ?? String(o.value ?? o.id) }
    : { value: o, label: String(o) });
  const stateSelectOptions = (scopeOptions.states || []).map(toOption);
  const zoneSelectOptions = (scopeOptions.zones || []).map(toOption);
  const facilityTypeSelectOptions = (scopeOptions.facility_types || []).map(toOption);
  const planSelectOptions = (scopeOptions.plans || []).map(toOption);

  const featureEnabled =
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === '1' ||
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === 'true';

  const hasActiveFilters = Boolean(search.trim() || statusFilter !== '');

  const filteredTariffs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tariffs.filter((tariff) => {
      const name = (tariff?.tariff_name ?? tariff?.name ?? '').toLowerCase();
      const category = (tariff?.category_name ?? '').toLowerCase();
      const matchesSearch = !query || name.includes(query) || category.includes(query);
      const active = tariff?.is_active != null ? Boolean(tariff.is_active) : null;
      const matchesStatus =
        statusFilter === ''
        || (statusFilter === 'active' && active === true)
        || (statusFilter === 'inactive' && active === false);
      return matchesSearch && matchesStatus;
    });
  }, [tariffs, search, statusFilter]);

  const activeCount = useMemo(
    () => tariffs.filter((tariff) => Boolean(tariff?.is_active)).length,
    [tariffs]
  );

  const totalItems = useMemo(
    () => tariffs.reduce((sum, tariff) => sum + (Number(tariff?.items_count) || 0), 0),
    [tariffs]
  );

  const scopedCount = useMemo(
    () => tariffs.filter((tariff) => tariff?.scope_state || tariff?.scope_zone_id || tariff?.scope_facility_type || tariff?.scope_plan_id).length,
    [tariffs]
  );

  const heroMetrics = useMemo(() => [
    {
      key: 'total',
      title: 'Standard tariffs',
      value: tariffs.length,
      color: '#3497D3',
      icon: <DollarOutlined />,
      subtitle: hasActiveFilters ? 'Matching filters' : 'All tariffs',
    },
    {
      key: 'active',
      title: 'Active',
      value: activeCount,
      color: '#49B870',
      icon: <CheckCircleOutlined />,
      subtitle: 'Currently active',
    },
    {
      key: 'items',
      title: 'Line items',
      value: totalItems,
      color: '#722ed1',
      icon: <UnorderedListOutlined />,
      subtitle: 'Across all tariffs',
    },
    {
      key: 'scoped',
      title: 'Scoped',
      value: scopedCount,
      color: '#faad14',
      icon: <CloseCircleOutlined />,
      subtitle: 'With scope rules',
    },
  ].filter((metric) => hasMetricValue(metric.value) || metric.key === 'total'), [
    tariffs.length,
    activeCount,
    totalItems,
    scopedCount,
    hasActiveFilters,
  ]);

  const openTariff = useCallback((record) => {
    const id = getTariffId(record);
    if (id) navigate(`/admin/tariffs/standard/${id}`, { state: { tariff: record } });
  }, [navigate]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  const columns = [
    { title: 'Name', dataIndex: 'tariff_name', key: 'tariff_name', ellipsis: true, render: (v, r) => v ?? r?.name ?? '—' },
    { title: 'Category', key: 'category', width: 140, render: (_, r) => r?.category_name ?? (r?.category_id != null ? `Category ${r.category_id}` : '—') },
    { title: 'Items', dataIndex: 'items_count', key: 'items_count', width: 90, align: 'right', render: (v) => (v != null ? Number(v).toLocaleString() : '—') },
    { title: 'Effective', dataIndex: 'effective_date', key: 'effective_date', width: 110, render: (v) => (v ? new Date(v).toLocaleDateString() : '—') },
    { title: 'Expiry', dataIndex: 'expiry_date', key: 'expiry_date', width: 110, render: (v) => (v ? new Date(v).toLocaleDateString() : '—') },
    { title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 80, render: (v) => (v != null ? (v ? 'Yes' : 'No') : '—') },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      render: (_, record) => {
        const id = getTariffId(record);
        if (!id) return null;
        return (
          <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); openTariff(record); }}>
            View items
          </Button>
        );
      },
    },
  ];

  if (!featureEnabled) {
    return (
      <div className="standard-tariff-disabled">
        <Alert
          type="info"
          message="Tariff module is currently disabled"
          action={<Button type="link" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>}
        />
      </div>
    );
  }

  return (
    <div className="standard-tariff-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">HMO Standard Tariff</h1>
            <p className="dash-masthead__meta">
              Default tariff that facilities can adopt — manage uploads and scope here
            </p>
          </div>
        </header>

        <section className="standard-tariff-overview dash-overview dash-overview--compact" aria-label="Tariff summary">
          <DashboardHero metrics={heroMetrics} loading={loading && !tariffs.length} />
        </section>

        {error ? (
          <Alert type="error" showIcon closable message="Error loading tariffs" description={error} onClose={() => setError(null)} />
        ) : null}

        <DashboardSection
          id="standard-tariff-directory"
          icon={<DollarOutlined />}
          title="Standard tariffs"
          subtitle={
            loading
              ? 'Loading…'
              : `${filteredTariffs.length} of ${tariffs.length} tariff${tariffs.length === 1 ? '' : 's'}`
          }
          collapsible={false}
          variant="content"
          action={(
            <div className="standard-tariff-toolbar standard-tariff-toolbar--inline">
              <Input
                className="standard-tariff-toolbar__search"
                size="small"
                placeholder="Search tariffs…"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
              <Select
                className="standard-tariff-toolbar__select"
                size="small"
                placeholder="Status"
                allowClear
                value={statusFilter || undefined}
                onChange={(value) => setStatusFilter(value ?? '')}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
              />
              <Button size="small" onClick={clearFilters} disabled={!hasActiveFilters}>
                Clear
              </Button>
              <Segmented
                size="small"
                value={view}
                onChange={setView}
                options={[
                  { label: 'List', value: 'list', icon: <AppstoreOutlined /> },
                  { label: 'Table', value: 'table', icon: <TableOutlined /> },
                ]}
              />
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchTariffs} loading={loading}>
                Refresh
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => { setUploadError(null); setUploadModalOpen(true); }}
              >
                Upload tariff
              </Button>
            </div>
          )}
        >
          {loading && !tariffs.length ? (
            view === 'table' ? (
              <TableSkeleton rows={6} columns={7} />
            ) : (
              <div className="standard-tariff-loading">
                <Spin size="large" />
              </div>
            )
          ) : filteredTariffs.length === 0 ? (
            <Empty
              description={
                tariffs.length === 0
                  ? 'No standard tariffs yet. Upload a CSV, TSV, or Excel file to get started.'
                  : 'No tariffs match the current filters.'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : view === 'table' ? (
            <div className="standard-tariff-table-wrap">
              <Table
                size="small"
                dataSource={filteredTariffs}
                columns={columns}
                rowKey={getTariffListRowKey}
                onRow={(record) => ({
                  onClick: () => openTariff(record),
                  style: getTariffId(record) ? { cursor: 'pointer' } : undefined,
                })}
                pagination={false}
              />
            </div>
          ) : (
            <div className="standard-tariff-listing-stack">
              {filteredTariffs.map((tariff) => (
                <StandardTariffListingCard
                  key={getTariffId(tariff) ?? tariff.tariff_name}
                  record={tariff}
                  onClick={() => openTariff(tariff)}
                />
              ))}
            </div>
          )}
        </DashboardSection>
      </div>

      <Modal
        title="Upload standard tariff"
        open={uploadModalOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => setUploadModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalOpen(false)}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploadLoading}
            onClick={handleUploadSubmit}
            disabled={!uploadFile || !uploadTariffName || !uploadEffectiveDate}
          >
            Upload
          </Button>,
        ]}
        width={520}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {uploadError && <Alert type="error" message={uploadError} />}
          <Alert type="info" message="Upload a CSV, TSV, or Excel file. Scope can be set here or later via Edit scope / dates." showIcon />
          <div>
            <Text strong>File (CSV, TSV, or Excel)</Text>
            <Input type="file" accept=".csv,.tsv,.xlsx,.xls" style={{ marginTop: 4 }} onChange={(e) => setUploadFile(e.target?.files?.[0] ?? null)} />
          </div>
          <div>
            <Text strong>Tariff name</Text>
            <Input placeholder="e.g. HMO Standard 2024" value={uploadTariffName} onChange={(e) => setUploadTariffName(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <div>
            <Text strong>Effective date</Text>
            <DatePicker value={uploadEffectiveDate} onChange={setUploadEffectiveDate} style={{ width: '100%', marginTop: 4 }} format="YYYY-MM-DD" />
          </div>
          <div>
            <Text strong>Expiry date (optional)</Text>
            <DatePicker value={uploadExpiryDate} onChange={setUploadExpiryDate} style={{ width: '100%', marginTop: 4 }} format="YYYY-MM-DD" />
          </div>
          <div>
            <Text strong>Category (optional)</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 2, marginBottom: 4 }}>
              If set, the file does not need a category column; all rows use this category.
            </Text>
            <Select
              placeholder="All categories (file must include category per row)"
              allowClear
              value={uploadCategoryId ?? undefined}
              onChange={(v) => setUploadCategoryId(v ?? undefined)}
              options={[{ value: 'all', label: 'All categories' }, ...categoryOptions]}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <Text strong>Scope (optional)</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 2, marginBottom: 6 }}>
              Limit where this tariff applies. You can also set or change scope later.
            </Text>
            {scopeOptionsLoading ? (
              <Spin size="small" style={{ display: 'block', marginTop: 8 }} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Select placeholder="Scope state" allowClear options={stateSelectOptions} value={uploadScopeState || undefined} onChange={(v) => setUploadScopeState(v ?? '')} style={{ width: '100%' }} />
                <Select placeholder="Scope zone" allowClear options={zoneSelectOptions} value={uploadScopeZoneId ?? undefined} onChange={(v) => setUploadScopeZoneId(v ?? '')} style={{ width: '100%' }} />
                <Select placeholder="Scope facility type" allowClear options={facilityTypeSelectOptions} value={uploadScopeFacilityType || undefined} onChange={(v) => setUploadScopeFacilityType(v ?? '')} style={{ width: '100%' }} />
                <Select placeholder="Scope plan" allowClear options={planSelectOptions} value={uploadScopePlanId ?? undefined} onChange={(v) => setUploadScopePlanId(v ?? '')} style={{ width: '100%' }} />
              </Space>
            )}
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default StandardTariffView;
